import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';

import { environment } from 'src/environments/environment';
import { CyclePackageRef, cyclePackageFields } from '../_helpers/cyclic-package';

// PMJAY facility/doctor lookups live on the separate "clinic" service
// (not this app's own apiUrl) and its responses come back AES-encrypted
// as { data: "<base64>" }, with the AES key itself embedded in that same
// base64 blob (base64(ciphertext + '.' + base64(key))) rather than a
// shared app secret - see vitalbase-connector's DecryptionService, which
// this mirrors, for the same scheme used elsewhere in this product.
const PMJ_API_BASE = 'https://clinic-devapi.vitalbase.in/api/v1/insurance/pmj';

// Given as a full URL (not environment.apiUrl-relative) - a different host
// (hcx.chipsyservices.com, no "-devapi") than this app's own apiUrl.
const HOSPITAL_ADD_WITH_DOCTOR_URL = 'https://hcx-devapi.chipsyservices.com/api/v1/admin/hospital/add-with-doctor';

// PMJAY patient lookup by facility - same host+base as PMJ_API_BASE above
// (confirmed via a real request/response), so it goes through the same
// decryptPmjResponse defensive unwrap as getPmjHospitals/getPmjDoctors below.
const PMJ_PATIENT_SEARCH_URL = `${PMJ_API_BASE}/patients`;

@Injectable({ providedIn: 'root' })
export class InsuranceLookupService {

    constructor(
        private router: Router,
        private http: HttpClient
    ) { }

    searchInsurance(phone: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/getInsuranceList`, { phone })
            .pipe(map(user => {
                return user;
            }));
    }

    eligibilityCheck(patientId: string, doctorId: string, hospitalId: string, purpose: string = 'validation', itemDetails?: {
        productCode: string,
        productDisplay: string,
        diagnosisCode: string,
        diagnosisDisplay: string,
        categoryCode: string,
        categoryDisplay: string,
        quantityValue: number,
        modifierCode?: string,
        modifierDisplay?: string
    }[]): Observable<any> {
        const formData = {
            patientId,
            doctorId,
            hospitalId,
            purpose,
            ...(itemDetails ? { items: itemDetails } : {})
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/checkEligibility`, formData)
            .pipe(map(user => {
                return user;
            }));
    }

    // New Admission "Fill Member Details" auto-fetch - when getInsuranceList
    // finds a policy (insuranceData) but no existing local patient record
    // (patientData is empty), this looks the beneficiary's own demographic
    // details (name/DOB/gender/phone/address) up on the HCX network itself
    // via a 'discovery' purpose CoverageEligibilityResponse, instead of
    // requiring the front-desk staff to type them in by hand. Confirmed (via
    // a manual Postman call against this same endpoint) to return the raw
    // FHIR Bundle - not the {status,data} wrapper shape validation/
    // auth-requirements purposes use elsewhere in this app - so the caller
    // must handle both shapes defensively.
    checkEligibilityDiscovery(abhanumber: string, memberid: string, payerid: string): Observable<any> {
        const formData = {
            abhanumber,
            memberid,
            payerid,
            purpose: 'discovery'
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/checkEligibility`, formData)
            .pipe(map(user => {
                return user;
            }));
    }

    patientRegister(patientname: string, patientdob: string, patientgender: string, aadhaarNumber: string, abhanumber: string,
        mobilenumber: string, memberid: string, payerid: string, policyname: string, policyid: string,
        // HMIS admission lookup (HmisAdmissionLookup.registrationFields) -
        // register() re-fetches it from HMIS to check the registration window.
        hmisAdmission?: object): Observable<any> {



        let data = {
            patientname,
            patientdob,
            patientgender,
            aadhaarNumber,
            abhanumber,
            mobilenumber,
            memberid,
            // register() reads payerId/policyId/policyName (the keys
            // registerNewPatient sends) - lowercase keys were silently dropped.
            payerId: payerid,
            policyName: policyname,
            policyId: policyid,
            ...(hmisAdmission || {})
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/register`, { ...data })
            .pipe(map(user => {
                return user;
            }));
    }

    submitPreAuth(
        patientId: string,
        billStartDate: string,
        billEndDate: string,
        items: any,
        totalAmount: number,
        diagnosis: any,
        procedure: any,
        hospitalId: string,
        preAuthType: any,
        content: any,
        doctorId: string,
        conditionCode: string,
        conditionValue: string,
        clinicalStatusCode: string,
        clinicalStatusValue: string,
        workFlowId: string,
        caseIdInternal: string,
        supportingInfo: any,
        payerDetails: { patientPMJAYId: string, payerCode: string, payerName: string, coverageValue: string },
        questionnaireResponses: any,
        documentReference: any,
        // Only set when this is a query-driven resubmit (workFlowId 19) of an
        // existing preauth - lets the backend attach it to that same preauth
        // chain instead of treating it as a brand new submission.
        correlationId?: string,
        preAuthRef?: string,
        // Newborn case (initial pre-auth only) - undefined/omitted for every
        // normal submission, so the request body is identical to today's.
        newbornDetails?: { name: string, gender: string, dob: string, birthCertificate: string, birthCertificateContentType?: string },
        // Face Auth only - the biometric refreshToken (see verifyFaceAuth),
        // sent as the x-user-token header so the backend can use it in place
        // of the normal access token for this submission (backend-owned
        // condition/refresh handling from here - never logged, never put in
        // the request body). Omitted entirely for Fingerprint/Iris and for
        // any submission where Face Auth wasn't used.
        xUserToken?: string,
        // Rejected PreAuth resubmission only (sent as workFlowId 12) - the rejected
        // preAuth record's own _id, so the backend links the new resubmission
        // record to it (resubmissionOf) instead of guessing by caseIdInternal.
        originalPreAuthId?: string,
        // Enhancement (workFlowId 13) reason - sent as the request's existing
        // `remark` field, which submitPreauth already accepts and saves on
        // the PreAuth record.
        remark?: string
    ): Observable<any> {
        const requestBody = {
            patientId: patientId,
            billStartDate: billStartDate,
            billEndDate: billEndDate,
            items: items,
            totalAmount: totalAmount,
            diagnosis: diagnosis,
            procedure: procedure,
            hospitalId: hospitalId,
            preAuthType: preAuthType,
            workFlowId: workFlowId,
            content: content,
            doctorId: doctorId,
            conditionCode: conditionCode,
            conditionValue: conditionValue,
            clinicalStatusValue: clinicalStatusValue,
            clinicalStatusCode: clinicalStatusCode,
            caseIdInternal: caseIdInternal,
            supportingInfo: supportingInfo,
            questionnaireResponses: questionnaireResponses,
            documentReference: documentReference,
            ...(correlationId ? { correlationId } : {}),
            ...(preAuthRef ? { preAuthRef } : {}),
            ...(originalPreAuthId ? { originalPreAuthId } : {}),
            ...(remark ? { remark } : {}),
            ...(newbornDetails ? { isNewborn: true, newbornDetails } : {}),
            ...payerDetails
        };

        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/submitPreauth`, requestBody,
            xUserToken ? { headers: new HttpHeaders({ 'x-user-token': xUserToken }) } : {})
            .pipe(map(user => {
                return user;
            }));
    }

    submitClaim(patientId: string,
        billStartDate: string,
        billEndDate: string,
        items: any,
        totalAmount: number,
        diagnosis: any,
        procedure: any,
        hospitalId: string,
        preAuthType: string,
        content: any,
        doctorId: string,
        conditionCode: string,
        conditionValue: string,
        workFlowId: string,
        caseIdInternal: string,
        supportingInfo: any,
        payerDetails: { patientPMJAYId: string, payerCode: string, payerName: string, coverageValue: string },
        questionnaireResponses: any,
        documentReference: any): Observable<any> {
        const requestBody = {
            patientId: patientId,
            billStartDate: billStartDate,
            billEndDate: billEndDate,
            items: items,
            totalAmount: totalAmount,
            diagnosis: diagnosis,
            procedure: procedure,
            hospitalId: hospitalId,
            preAuthType: preAuthType,
            workFlowId: workFlowId,
            content: content,
            doctorId: doctorId,
            conditionCode: conditionCode,
            conditionValue: conditionValue,
            caseIdInternal: caseIdInternal,
            supportingInfo: supportingInfo,
            questionnaireResponses: questionnaireResponses,
            documentReference: documentReference,
            // Confirmed-working reference payload for this endpoint has no
            // correlationId/preAuthRef fields at all - including them (as this
            // app briefly did) is a likely cause of "Invalid discharge stage"
            // rejections, so they're deliberately omitted here.
            ...payerDetails
        };

        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/hcxClaim`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }


    submitReprocess(patientId: string,
        billStartDate: string,
        billEndDate: string,
        items: any,
        totalAmount: number,
        diagnosis: any,
        procedure: any,
        hospitalId: string,
        preAuthType: string,
        workFlowId: string,
        content: any,
        doctorId: string,
        conditionCode: string,
        conditionValue: string,
        clinicalStatusCode: string,
        clinicalStatusValue: string,
        caseIdInternal: string,
        supportingInfo: any,
        payerDetails: { patientPMJAYId: string, payerCode: string, payerName: string, coverageValue: string },
        questionnaireResponses: any,
        documentReference: any,
        correlationId: string,
        preAuthRef: string): Observable<any> {
        const requestBody = {
            patientId: patientId,
            billStartDate: billStartDate,
            billEndDate: billEndDate,
            items: items,
            totalAmount: totalAmount,
            diagnosis: diagnosis,
            procedure: procedure,
            hospitalId: hospitalId,
            preAuthType: preAuthType,
            workFlowId: workFlowId,
            content: content,
            doctorId: doctorId,
            conditionCode: conditionCode,
            conditionValue: conditionValue,
            clinicalStatusCode: clinicalStatusCode,
            clinicalStatusValue: clinicalStatusValue,
            caseIdInternal: caseIdInternal,
            supportingInfo: supportingInfo,
            questionnaireResponses: questionnaireResponses,
            documentReference: documentReference,
            correlationId: correlationId,
            preAuthRef: preAuthRef,
            ...payerDetails
        };

        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/hcxReprocess`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }

    cancelPreAuth(patientId: string,
        reasonCode: string,
        reasonDisplay: string,
        caseIdInternal: string,
        claimNumber: string,
        intimationNumber: string): Observable<any> {
        const requestBody = {
            patientId: patientId,
            reasonCode: reasonCode,
            reasonDisplay: reasonDisplay,
            caseIdInternal: caseIdInternal,
            claimNumber: claimNumber,
            intimationNumber: intimationNumber
        };
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/cancelPreauth`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }

    // Auto-triggered when submitPreAuth() (POST /admin/hcx/submitPreauth)
    // rejects with "Beneficiary is having an active preauthorization request
    // at this hospital..." (see submit-pre-auth.component.ts's
    // submitPreAuthorization() error handler) - a deliberately different,
    // narrower payload than cancelPreAuth() above (TC-10's manual Cancel
    // Registration flow): no caseIdInternal/intimationNumber, but
    // workFlowId:"122" instead - this exact shape is the confirmed working
    // request for this specific conflict response. reasonCode is the NHCX
    // handbook's own Appendix B code (https://nrces.in/ndhm/fhir/r4/
    // CodeSystem/ndhm-reason-code) matching reasonDisplay's "Treatment plan
    // changed during hospitalization." - unlike cancelPreAuth() above, which
    // hardcodes the generic 'other' since no real reason list existed yet.
    cancelActivePreAuth(patientId: string, reasonCode: string, reasonDisplay: string, claimNumber: string): Observable<any> {
        const requestBody = {
            patientId: patientId,
            reasonCode: reasonCode,
            reasonDisplay: reasonDisplay,
            claimNumber: claimNumber,
            workFlowId: '122'
        };
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/cancelPreauth`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }

    // TC-06 (Registration Cancel/Delete) - distinct from cancelPreAuth above
    // (TC-10), which cancels the NHCX preauth submission itself. This cancels
    // the registration/case (patientPolicyDetails record) identified by the
    // same id used throughout this app as "patientId" elsewhere.
    // Optional Dialysis Document for one cycle of an approved cyclic procedure
    // (View Details' Cyclic Procedure Bio-auth table). admissionId is the
    // patientPolicyDetails id; the cycle is (preAuthId, itemCode, cycleNumber).
    // Uploading again for the same cycle replaces its document. Both answer
    // with the admission's updated cycleDocuments list.
    uploadCycleDocument(admissionId: string, cycle: { preAuthId: string, itemCode: string, cycleNumber: number },
        file: { fileName: string, contentType: string, data: string }): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/cycle-document/${admissionId}`, { ...cycle, ...file })
    }

    removeCycleDocument(admissionId: string, cycle: { preAuthId: string, itemCode: string, cycleNumber: number }): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/cycle-document/${admissionId}/remove`, cycle)
    }

    cancelRegistration(registrationId: string, reason: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/cancel-registration/${registrationId}`, { reason })
    }

    // document: optional supporting attachment (PDF/DOCX/PNG/JPEG, base64
    // without the data: prefix) - sent as the Task's "document" input.
    submitErroneousClaim(patientId: string,
        hospitalId: string,
        amount: string,
        caseIdInternal: string,
        document?: { contentType: string, data: string, title: string } | null): Observable<any> {
        const requestBody: any = {
            patientId: patientId,
            hospitalId: hospitalId,
            amount: amount,
            caseIdInternal: caseIdInternal
        };
        if (document?.data) {
            requestBody.document = document;
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/submitErroneousClaim`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }

    // The Erroneous Claim amount as submitErroneousClaim recalculates it, for
    // the same patientId/caseIdInternal - View Details shows this instead of
    // calculating its own. A refusal comes back as data (ok false, reason).
    getErroneousClaimAmount(patientId: string, caseIdInternal: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/erroneousClaimAmount`, { patientId, caseIdInternal })
    }

    // Payer query on the existing Erroneous Claim: the query and the
    // backend-recalculated amount (previous vs corrected).
    getErroneousClaimQuery(patientId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/erroneousClaimQuery`, { patientId })
    }

    // Answers that query with the corrected amount (the backend recalculates
    // and must match it) and the hospital's remark - never a new Erroneous Claim.
    respondErroneousClaimQuery(patientId: string, amount: string, remark: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/respondErroneousClaimQuery`, { patientId, amount, remark })
    }

    // Claim-section Reprocess (rejected claim) - a separate, minimal payload
    // to the same admin/hcx/hcxReprocess endpoint already used by
    // submitReprocess() above for preauth/final/enhancement reprocess. Not
    // reusing that method - its signature/payload shape (items, diagnosis,
    // procedure, supportingInfo, etc.) is entirely different and unrelated to
    // this document-only claim reprocess. reasonCode/reasonDisplay
    // deliberately omitted for now - to be added later if required.
    submitClaimReprocess(patientId: string,
        hospitalId: string,
        caseIdInternal: string,
        document: { contentType: string, data: string, title: string }): Observable<any> {
        const requestBody = {
            patientId: patientId,
            hospitalId: hospitalId,
            caseIdInternal: caseIdInternal,
            document: document
        };
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/hcxReprocess`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }


    getDoctorList(search: string, page: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/doctor/list?search=${search}&page=${page}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    deleteDoctor(id: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/doctor/delete/${id}`, {})
    }

    getPatientList(search: string, page: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/list?search=${search}&page=${page}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    addDoctors(data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/doctor/add`, data)
            .pipe(map(user => {
                return user;
            }));
    }


    updateDoctors(data: any, id: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/doctor/update/${id}`, data)
            .pipe(map(user => {
                return user;
            }));
    }

    getHospitalList(search: string, page: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/list?search=${search}&page=${page}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    deleteHospital(id: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/delete/${id}`, {})
    }

    addHospital(data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/add`, data)
            .pipe(map(user => {
                return user;
            }));
    }

    updateHospital(data: any, id: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/update/${id}`, data)
            .pipe(map(user => {
                return user;
            }));
    }

    getInsuranceDetailSubmit(patienId: string, patientRefNumber: any): Observable<any> {
        const data = {
            patientId: patienId,
            patientRefNumber: patientRefNumber
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/getInsurancePlan`, data)
            .pipe(map(user => {
                return user;
            }));
    }


    getAllInsuranceRequests(page: string, search: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/insurance-details?page=${page}&search=${search}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    getDoctorsDropdown(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/doctor/dropdown`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    getHospitalDropdown(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/dropdown`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    private decryptPmjResponse(response: any): any {
        // Some environments return this endpoint already as plain JSON
        // (response.data is the hospitals/doctors array itself) while
        // others return it AES-encrypted as { data: "<base64>" } - only
        // the latter (a string payload) needs decrypting.
        if (!response || typeof response.data !== 'string') {
            return response
        }
        const decoded = atob(response.data + '=')
        const [cipherText, encodedKey] = decoded.split('.')
        const secretKey = atob(encodedKey)
        const bytes = CryptoJS.AES.decrypt(cipherText, secretKey)
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
    }

    // Validate Coverage Eligibility dialog: Hospital dropdown is populated
    // first from the PMJAY facility list; the chosen facilityId then scopes
    // the Doctor dropdown (getPmjDoctors) to that facility.
    getPmjHospitals(): Observable<any> {
        return this.http.get<any>(`${PMJ_API_BASE}/hospitals`)
            .pipe(map(response => this.decryptPmjResponse(response)))
    }

    getPmjDoctors(facilityId: string): Observable<any> {
        return this.http.get<any>(`${PMJ_API_BASE}/doctors`, { params: { facilityId } })
            .pipe(map(response => this.decryptPmjResponse(response)))
    }

    // Same endpoint as getPmjDoctors, scoped further to one doctorId - called
    // once both Hospital and Doctor are picked in the Validate Coverage
    // Eligibility dialog, before Validate Eligibility itself is submitted.
    getPmjDoctorDetails(facilityId: string, doctorId: string): Observable<any> {
        return this.http.get<any>(`${PMJ_API_BASE}/doctors`, { params: { facilityId, doctorId } })
            .pipe(map(response => this.decryptPmjResponse(response)))
    }

    // Fired immediately once Validate Coverage Eligibility resolves as active
    // (see pollEligibilityValidation's inforce===true branch) - looks up this
    // beneficiary's existing PMJAY patient record at the validated facility,
    // searched by their ABHA number (abhanumber2, from that same
    // EligibilityCheck record). Response also feeds the Attendant Details
    // autofill (docs[0].emergencyDetails) once back in the component.
    searchPmjPatients(facilityId: string, search: string): Observable<any> {
        return this.http.get<any>(PMJ_PATIENT_SEARCH_URL, { params: { facilityId, search, page: '' } })
            .pipe(map(response => this.decryptPmjResponse(response)))
    }

    // Hospital record by its own _id - carries hospitalFacilityId (the HIMS/PMJ
    // facility), which the dropdown list does not.
    getHospitalById(hospitalId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hospital/list-byId/${hospitalId}`, {})
    }

    // HIMS IP patient details (incl. data.dischargeSummary) via this app's own
    // backend proxy (routes/clinicproxy.js -> clinic service), so no clinic
    // URL/credentials live in the browser. The proxy wraps the clinic body as
    // { status, data: <clinic body> }; a clinic body that comes back
    // AES-encrypted is unwrapped the same way as the other PMJ lookups.
    getHimsIpPatientDetails(mobile: string, facilityId: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/clinicproxy/pmj/ip-patient-details`, { params: { mobile, facilityId } })
            .pipe(map(response => ({ ...response, data: this.decryptPmjResponse(response?.data) })))
    }

    // Mirrors the just-fetched PMJ hospital/doctor (getPmjDoctorDetails) into
    // this app's own hospital/doctor records. Plain (unencrypted) request -
    // this hits this app's own backend, not the clinic-devapi service.
    addHospitalWithDoctor(hospitals: {
        hospitalName: string, hospitalFacilityId: string, hospitalPhoneNo: string, hospitalEmail: string,
        hospitalCity: string, hospitalCountry: string, hospitalAddress: string,
        hospitalLongitude: number | null, hospitalLatitute: number | null, hospitalPostalCode: string
    }[], doctors: {
        fullname: string, dob: string, gender: string, mobilenumber: string, medicallicensenumber: string
    }[]): Observable<any> {
        return this.http.post<any>(HOSPITAL_ADD_WITH_DOCTOR_URL, { hospitals, doctors })
            .pipe(map(response => response))
    }

    // Searchable Category picker for the Stage 2 (auth-requirements) eligibility
    // tab - replaces the old InsurancePlan-bundle-derived category list. POST
    // method, search passed as a query param (not body) per backend team.
    getPackageCategoryDropdown(search: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/package-category/dropdown`, {}, {
            params: { search }
        }).pipe(map(user => {
            return user;
        }));
    }

    // Searchable Package picker, scoped to the selected category - replaces the
    // old InsurancePlan-bundle-derived package list. POST method, categoryCode
    // and search passed as query params (not body) per backend team.
    //
    // parentProcedureCode/includeImplants are optional - passed only when
    // fetching implant packages for a specific parent procedure (see
    // addImplant() in submit-pre-auth.component.ts). Without them the backend
    // excludes implant/add-on package records by default, matching prior
    // behaviour for every other existing caller of this method.
    getInsurancePackageDropdown(categoryCode: string, search: string, parentProcedureCode?: string, includeImplants?: boolean): Observable<any> {
        const params: any = { categoryCode, search }
        if (parentProcedureCode) {
            params.parentProcedureCode = parentProcedureCode
        }
        if (includeImplants) {
            params.includeImplants = 'true'
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/insurance-package/dropdown`, {}, {
            params
        }).pipe(map(user => {
            return user;
        }));
    }

    // Full package details (conditions, mandatoryDocuments, wardRates) for a
    // package picked from getInsurancePackageDropdown - replaces the old
    // InsurancePlan-bundle-derived ward/stratification/quantity-condition lookup.
    getInsurancePackageDetails(packageId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/insurance-package/list-byId/${packageId}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    // This app's OWN internal questionnaire-definition record for a payer
    // auth-requirements questionnaire (e.g. "100003" - Approver Checklist) -
    // NOT a direct fetch of the payer's own fullUrl (submit-pre-auth.
    // component.ts's "Open Questionnaire" action calls this, keyed by the
    // authorizationSupporting entry's own coding[0].code). POST with an
    // empty body, same convention as getInsurancePackageDetails/list-byId
    // above. Returns {status, data: {item: [{linkId, text, type, required,
    // answerOption}]}}.
    getQuestionnaireById(questionnaireId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/questionnaire/list-byId/${questionnaireId}`, {})
            .pipe(map(user => {
                return user;
            }));
    }


    getPreAuthData(preAuthId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/preAuth-details/${preAuthId}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    getDashboardData(type: string, useCase: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/dashboad/list`, { type, useCase })
            .pipe(map(user => {
                return user;
            }));
    }

    getDashboardCount(type: string, useCase: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/dashboad/count`, { type, useCase })
            .pipe(map(user => {
                return user;
            }));
    }

    getNotification(limit: number | null): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/notification/list`, { limit })
            .pipe(map(user => {
                return user;
            }));
    }

    clearAllnotification(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/notification/update-all`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    clearSinglenotification(id: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/notification/update/${id}`, {})
            .pipe(map(user => {
                return user;
            }));
    }

    // TC-05: audit entry for the Copayment consent upload - the one
    // genuine, user-initiated wallet transaction TC-05 requires an audit
    // trail for. Records the logged-in admin's own ID (server-side, via
    // req.auth._id). Does not compute or persist a wallet balance itself -
    // amount is just carried through from whatever the existing NHCX
    // eligibility flow already resolved (see submit-pre-auth.component.ts's
    // maybeRefreshCopayWalletBalance). consentDocument (the Copayment
    // consent PDF, base64) is mandatory here - reuses the existing
    // uploadContentToS3 convention server-side, same as consentForm/
    // birthCertificate elsewhere.
    logWalletEvent(patientId: string, eventType: string, consentDocument: string, amount?: number | null, message?: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/notification/log-wallet-event`, { patientId, eventType, consentDocument, amount, message })
    }

    submitClaimPreAuth(patientId: string, billStartDate: string, billEndDate: string, content: any): Observable<any> {
        const requestBody = {
            patientId: patientId,
            billStartDate: billStartDate,
            billEndDate: billEndDate,
            claimType: 'claim',
            workFlowId: '15',
            content: content,
        };

        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/hcxClaim`, requestBody)
            .pipe(map(user => {
                return user;
            }));
    }

    // Re-runs a WF30 Payment Notice's payment/details -> payment/utr after a
    // failure (backend: abdmProxy.controller.js retryPaymentNoticeApis).
    retryPaymentNoticeApis(correlation_id: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/payment/notice/retry`, { correlation_id })
    }

    // Resolves a WF30 payment/utr the payer may already have accepted - never
    // resubmits it (backend: abdmProxy.controller.js reconcilePaymentNoticeUtr).
    reconcilePaymentNoticeUtr(correlation_id: string, confirmedOutcome?: 'accepted' | 'not_accepted', note?: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/payment/notice/reconcile`, { correlation_id, confirmedOutcome, note })
    }

    acknowledgePayment(patientId: string, correlation_id: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/payment_onrequest`, { patientId, correlation_id })
            .pipe(map(user => {
                return user;
            }));
    }

    // New Admission
    // payerid (optional): Member ID search's selected payer participantcode,
    // used by the backend only for NHCX-1016 policy discovery.
    searchNewInsurance(type: string, value: any, payerid?: string) {
        const formData = {
            type: type,
            value: value,
            ...(payerid ? { payerid } : {})
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/getInsuranceList`, formData)
    }

    // Polls the Get Policy discovery fallback getInsuranceList starts (202)
    // when /get/policies has no linked policy for a Member ID yet. 202 while
    // discovery/validation is in flight, 200 with the same
    // { insuranceData, patientData } shape as searchNewInsurance once linked.
    getPolicyDiscoveryStatus(memberId: string, correlationId: string) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/policyDiscoveryStatus`, {
            memberId,
            correlation_id: correlationId
        })
    }

    registerNewPatient(patientname: any, patientgender: any, patientdob: any, mobilenumber: any,
        email: any, memberid: any, abhanumber: any, address: any, payerId: any, policyId: any,
        policyName: any, patientId: any, consentForm?: any,
        // Identifies WHICH consent form this is (e.g. "Medical Superintendent
        // Declaration Form (During Admission)") - backend persists it
        // alongside consentForm (see patient.controller.js's register(),
        // consentFormData.documentName) on both the Patient and
        // patientPolicyDetails documents. Only meaningful when consentForm
        // is actually present - omitted (like consentForm) when the
        // Consent Form option wasn't used for this registration.
        consentFormName?: string, abhanumber2?: any,
        // Only the txnId (not sensitive - already visible in the QR/polling
        // flow) - the actual biometric token never leaves the backend (see
        // faceAuthVerify/pendingBiometricAuth.model.js). Presence of this
        // field is what tells register() "Face Auth was used for this
        // registration", matching the fingerprint/iris/face flags it already
        // expects.
        //TC04
        faceAuthTxnId?: string,
        // Only true when fingerprint is what actually authenticated this
        // registration (see new-admission.component.ts's
        // fingerprintAuthenticatedForRegistration) - mirrors faceAuthTxnId's
        // role below, just for the fingerprint method instead of face.
        fingerprint?: boolean,
        // TC-04: grouped into one trailing object instead of more positional
        // params - attendant/registrationType/child fields all arrived at
        // once and a 7-positional-arg tail would be unreadable and easy to
        // mis-order at the call site.
        admissionDetails?: {
            attendantName: string,
            attendantRelationship: string,
            attendantMobile: string,
            registrationType: 'Planned' | 'Emergency',
            medicoLegalCase?: 'Yes' | 'No',
            incidentDate?: string,
            firDate?: string,
            firReport?: string,
            firReportName?: string,
            biometricTxnId?: string,
            biometricAuthenticatedAt?: string,
            initialAssessmentDocument?: string,
            emergencyCertificateDocument?: string,
            isChildRegistration?: boolean,
            parentPmjayId?: string,
            childDob?: string,
            childName?: string,
            gender?: string,
            birthCertificate?: string,
            // HMIS patient-list admission (see new-admission's hmisAdmission) -
            // register() re-checks the PM-JAY registration window against it.
            hmisIpdId?: string,
            hmisAdmissionDate?: string,
            hmisIpdStatus?: string
        }) {
        const formData = {
            patientname: patientname,
            patientgender: patientgender,
            patientdob: patientdob,
            mobilenumber: mobilenumber,
            email: email,
            memberid: memberid,
            abhanumber: abhanumber,
            address: address,
            payerId: payerId,
            policyId: policyId,
            policyName: policyName,
            patientId: patientId,
            ...(consentForm ? { consentForm } : {}),
            ...(consentForm && consentFormName ? { consentFormName } : {}),
            ...(abhanumber2 ? { abhanumber2 } : {}),
            ...(faceAuthTxnId ? { face: true, faceAuthTxnId } : {}),
            ...(admissionDetails || {}),
            ...(fingerprint ? { fingerprint: true } : {})
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/register`, formData)
    }

    getPatientHistory(id: string, search: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/patient-history/${id}?search=${search}`, {})
    }

    searchManualEntry(policyId: string, payerId: any) {
        const formData = {
            policyId: policyId,
            payerId: payerId
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/patient/patient-exists-manual`, formData)
    }

    // Step 1 of ABHA biometric registration verification (Fingerprint/Face/Iris) -
    // starts the auth transaction and returns a txnId that the subsequent local
    // RD-service capture + verify call must reference.
    initBiometricAuth(payerId: string, loginId: string, authMode: 'FINGERPRINT' | 'IRIS'): Observable<any> {
        // ABDM expects the 14-digit ABHA number as "91-1280-1554-0312", not
        // the plain digit string it's stored as (policy.abhanumber) - only
        // reformat when it's genuinely a bare 14-digit number, so an
        // already-hyphenated or unexpected-length value passes through as-is.
        const digits = (loginId || '').replace(/\D/g, '')
        const formattedLoginId = digits.length === 14
            ? `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}-${digits.slice(10, 14)}`
            : loginId
        const body = {
            scope: ['abha-login', 'aadhaar-bio-verify'],
            loginHint: 'abha-number',
            loginId: formattedLoginId,
            otpSystem: 'aadhaar',
            authMode: authMode
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/abha/biometric/auth/init`, body)
    }

    // Step 2 - verifies the PID block captured locally from the RD Service
    // against the txnId from initBiometricAuth(). ABDM's verify contract
    // (confirmed via reference payload) needs authData nested per method -
    // NOT a bare string - and no top-level txnId (it lives inside
    // authData.bio.txnId etc). FINGERPRINT -> authData.bio.fingerPrintAuthPid
    // is the only path actually exercised today (see captureBiometricPid in
    // new-admission.component.ts - iris capture isn't implemented there
    // yet), so the iris branch below follows the same shape by analogy but
    // is unconfirmed/unreachable until that capture path exists.
    //
    // Face used to have an untested "by analogy" branch here too, but Face
    // Auth is a completely different ABDM flow/gateway (QR + Aadhaar verify,
    // no local hardware capture at all - see initFaceAuth/captureFaceAuthPid/
    // verifyFaceAuth below and NHCX_Faceauth_Implementation_Steps.docx), so
    // it's been removed from this method entirely rather than left as a
    // second, conflicting "FACE" path under the same authMode value.
    //
    // The reference payload also always includes an otp block alongside bio,
    // using a fixed value "123456" - this matches ABDM's sandbox (apisbx)
    // dummy-OTP convention, not a real user-entered OTP. There's no OTP
    // entry UI in this flow yet, so this is hardcoded to match the sandbox
    // reference - MUST be replaced with a real OTP source before this is
    // ever pointed at anything beyond the sandbox.
    //
    // process/payerId are sent to the backend proxy in the body, which then
    // forwards them as HTTP headers to ABDM (not body fields there) - see
    // biometricAuthVerify in abdmProxy.controller.js.
    // patientPolicyDetailsId/cycleNumber/caseIdInternal: same purpose as
    // verifyFaceAuth's own (see its comment) - Cyclic Preauth's per-cycle
    // Fingerprint verify needs these forwarded too so the backend actually
    // persists this cycle's authenticatedAt to biometricAuthLog (PAYR-1367 -
    // without patientPolicyDetailsId, biometricAuthVerify's own write guard
    // never fires, so the real ABDM-verified timestamp was silently never
    // recorded, and the Claim payload fell back to a purely local date with
    // no corresponding entry in ABDM's own biometric ledger). Optional/
    // omitted for every other existing caller (registration-time Fingerprint
    // has neither an admission nor a cycle yet), so their behavior is
    // unchanged.
    // cycleStage: cyclic procedure Check-in/Check-out (View Details' per-cycle
    // Bio-auth) - validated and recorded by the backend (see
    // abdmProxy.controller.js cyclicPrecheck/persistCycleEvent). Omitted by
    // every other caller, whose behaviour is unchanged.
    // cyclePackage: the cyclic package (itemCode, and its PreAuth record) the
    // cycle belongs to - an admission can have more than one cyclic package,
    // each with its own Cycle 1, 2, ...
    verifyBiometricAuth(txnId: string, pidData: string, authMode: 'FINGERPRINT' | 'IRIS', payerId: string, process: 'Preauth' | 'Discharge', patientPolicyDetailsId?: string, cycleNumber?: number, caseIdInternal?: string, cycleStage?: 'CHECK_IN' | 'CHECK_OUT', cyclePackage?: CyclePackageRef): Observable<any> {
        const bioBlock = authMode === 'FINGERPRINT'
            ? { txnId, fingerPrintAuthPid: pidData }
            : { txnId, irisAuthPid: pidData }
        const authData = { authMethods: ['bio'], bio: bioBlock, otp: { txnId, otpValue: '123456' } }
        const body = {
            scope: ['abha-login', 'aadhaar-bio-verify'],
            authData,
            authMode,
            payerId,
            process,
            ...(patientPolicyDetailsId ? { patientPolicyDetailsId } : {}),
            ...(cycleNumber !== undefined ? { cycleNumber } : {}),
            ...(caseIdInternal ? { caseIdInternal } : {}),
            ...(cycleStage ? { cycleStage } : {}),
            ...cyclePackageFields(cyclePackage)
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/abha/biometric/auth/verify`, body)
    }

    // Face Auth (QR + Aadhaar verify) - NHCX_Faceauth_Implementation_Steps.docx.
    // A distinct ABDM flow/gateway from initBiometricAuth/verifyBiometricAuth
    // above: no local hardware capture, no ABHA number - the user scans a QR
    // (built from the txnId this returns) in the Sandbox ABHA App, and
    // verification happens via Aadhaar + Aadhaar-linked mobile instead.
    initFaceAuth(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/abha/biometric/faceauth/init`, {})
    }

    // Single status check (PENDING/COMPLETE) - the caller (see
    // pollFaceAuthCapture in new-admission.component.ts) is responsible for
    // the polling interval/timeout, this just proxies one check per call.
    captureFaceAuthPid(txnId: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/abha/biometric/capture/pid`, { txnId })
    }

    // Aadhaar is sent to the backend as plain digits over the same
    // authenticated session every other API call already uses - encryption
    // (RSA/ECB/OAEPWithSHA-1AndMGF1Padding) happens server-side (see
    // helpers/aadhaar_encryption.js on the backend), never in the browser.
    //
    // patientPolicyDetailsId: only passed when the admission already exists
    // (Cyclic Preauth's per-cycle Face Auth - submit-pre-auth.component.ts's
    // this.patientId IS the patientPolicyDetails._id already) - the backend
    // then updates that admission's biometricAuth directly instead of using
    // the registration-time pendingBiometricAuth bridge.
    // cycleNumber/caseIdInternal: Cyclic Preauth's per-cycle Face Auth only
    // (see verifyCycleFaceAuthNow in submit-pre-auth.component.ts) - lets the
    // backend record which cycle/case this biometric verification belongs
    // to. Omitted for registration-time Face Auth, where neither exists yet.
    // cyclePackage: as verifyBiometricAuth's.
    verifyFaceAuth(txnId: string, aadhaar: string, mobile: string, payerId: string, process: 'Preauth' | 'Discharge', patientPolicyDetailsId?: string, cycleNumber?: number, caseIdInternal?: string, cycleStage?: 'CHECK_IN' | 'CHECK_OUT', cyclePackage?: CyclePackageRef): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/abdmproxy/abha/biometric/v2/auth/verify`, {
            txnId, aadhaar, mobile, payerId, process,
            ...(patientPolicyDetailsId ? { patientPolicyDetailsId } : {}),
            ...(cycleNumber !== undefined ? { cycleNumber } : {}),
            ...(caseIdInternal ? { caseIdInternal } : {}),
            ...(cycleStage ? { cycleStage } : {}),
            ...cyclePackageFields(cyclePackage)
        })
    }

    getPayerIdDropdown(role: any, fromdate: any, todate: any, search?: string): Observable<any> {
        const formData = {
            role: role,
            fromdate: fromdate,
            todate: todate,
            entitytype: 'GOV',
            ...(search ? { search } : {})
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/hcx/getPayerId`, formData)
    }
}
